"use client";

/**
 * ComposerInlineCard — the inline surface for composer sends routed through
 * the composer gateway (useComposerConverse).
 *
 * Redesign 2026-07-16 (founder-approved mockup:
 * mockups/composer-inline-redesign-2026-07-16.html): a BOARD-NATIVE presence
 * card in the porcelain idiom — white card, corner bloom, mono kicker — whose
 * bloom walks through presence colors as the turn progresses:
 *
 *   thinking/working  → thin strip, notch thinking lavender (#b591ff),
 *                       shimmer text only; the copy walks per tool_started
 *                       event (Thinking → Searching your tasks → …)
 *   answering         → Bash's amber (--hb-name), ink markdown on white
 *   task receipt      → green; REPLACES the old router.push yank — the card
 *                       is the door (click opens the task), shows live status
 *                       + the model actually running it
 *   saved to Nami     → Nami emerald, auto-dismisses after the confirm beat;
 *                       a save that asks a question (`say`) stays up
 *   not sent          → red kicker/bloom, ink retry button (classic lane)
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import type { ComposerPanelKind, ToolName } from "@bash/shared";
import type { ConverseUiState } from "@/components/composer/useConverse";
import {
  labelForModel,
  shortLabelForModel,
} from "@/components/ui/model-catalog";
import { useComposerDotsStore } from "@/stores/composerDotsStore";
import { useHistoryStore } from "@/stores/historyStore";
import { useTaskStore } from "@/stores/taskStore";
import {
  STATUS_WORD,
  taskLiveStatus,
  type TaskLiveStatus,
} from "@/lib/task-live-status";
import { renderMarkdown } from "@/lib/markdown";
import { deckDraws, DRAWN_KINDS } from "@/components/composer/panels/drawn";
import { PanelDeck } from "@/components/composer/panels/PanelDeck";
import { undoRunApi } from "@/lib/composer/undo-client";

interface ComposerInlineCardBaseProps {
  /** Never 'idle' when rendered — the host gates on it. */
  ui: ConverseUiState;
  question: string;
  onDismiss: () => void;
  /** Credits-state door — the plan page. */
  onUpgrade?: () => void;
  /** A later step's failure on a routed turn (the Line): the receipt stays,
   *  with this line beneath it, so a created task is never hidden by what
   *  went wrong after it. A node, so an empty wallet's line can carry its
   *  Top up door. */
  trouble?: ReactNode | null;
  /** The × on one panel (composer panels, spec 2026-09-19 §3.3); absent on a
   *  host that never draws one (the Line's consumer). */
  onClosePanel?: (kind: ComposerPanelKind) => void;
}

/** A retryable card must carry its retry (the classic lane's re-send); a
 *  card with nothing to retry (the Line: the ask was spoken and Bash already
 *  said it did not go through) must not. The error card then keeps only
 *  Dismiss, and its copy promises no retry. */
type ComposerInlineCardRetryProps =
  | { retryable: false; onRetryClassic?: undefined }
  | {
      retryable?: true;
      /** Error-state retry — re-sends the ask via the classic task lane. */
      onRetryClassic: () => void;
    };

type ComposerInlineCardProps = ComposerInlineCardBaseProps &
  ComposerInlineCardRetryProps;

// The "Saved to Nami" confirm auto-dismisses after this beat.
const NAMI_AUTODISMISS_MS = 2200;

/** The receipt lingers this long, then DISSOLVES into its dot on the composer
 *  rim (founder 07-16) — long enough to read and click, never squatting. */
const RECEIPT_LINGER_MS = 5000;

/** Notch thinking lavender — notch.html STATES.thinking glow rgba(181,145,255). */
const LAVENDER = "#b591ff";

/**
 * Honest narration per tool (founder 07-16: "if searching show searching, if
 * creating task do that") — rendered from the tool_started events already on
 * the wire, never a fake spinner. Unknown tools fall back to 'Working'.
 */
const NARRATION: Partial<Record<ToolName, string>> = {
  fetch_recent_tasks: "Searching your tasks",
  show_tasks: "Searching your tasks",
  create_task: "Creating your task",
  create_computer_task: "Creating your task",
  follow_up_task: "Updating your task",
  stop_task: "Stopping your task",
  save_to_nami: "Handing this to Nami",
  recall_from_nami: "Asking Nami",
  task_output: "Reading what it produced",
  task_state: "Checking how it is going",
  task_creations: "Looking at what it made",
  task_transcript: "Reading how it went",
  web_search: "Searching the web",
  web_fetch: "Reading the page",
  control_app: "Opening it for you",
  control_computer: "Starting on your Mac",
  run_verb: "On it",
  os_apps: "Looking at your Mac",
  os_snapshot: "Looking at your Mac",
  os_find: "Looking at your Mac",
  os_look: "Looking at your screen",
  os_selection: "Reading your selection",
  os_pointer: "Checking what you're pointing at",
  os_read: "Reading your screen",
  os_ocr: "Reading the text on your screen",
  // The catalog's search door (2a, review M4), so a catalog turn names the step instead of
  // falling to "Working". Its sibling run_tool takes no row on purpose: the server Line says
  // "Working on it." for a run it has no target for, and "Working" is this table's word for the
  // same thing. Narrating a run by its family and effect the way the Line does needs the
  // command-k reducer to carry the event's target, which R-2a-16 deliberately left alone.
  // R-2a-55 (2a.4): the Line's own words, so the typed card and the Line say the same thing;
  // "Looking for the right tool" came back from the live model as a tool gone missing.
  find_tools: "Picking the tool",
  // T11: the search modes' lookup of a tool by id is the same moment as picking one.
  get_tool_schemas: "Picking the tool",
};

export function narrationForTool(tool: ToolName | undefined): string {
  return (tool && NARRATION[tool]) || "Working";
}

/** Dot color per status — green is the card's own idiom for "moving"; the
 *  exceptions are the states that mean something else. */
const RECEIPT_DOT_COLOR: Partial<Record<TaskLiveStatus, string>> = {
  needs: "var(--kind-amber)",
  stopped: "var(--fg-faint)",
  out_of_credits: "var(--fg-faint)",
  error: "var(--kind-red)",
};

/** Settled statuses stop the pulse. */
const RECEIPT_SETTLED = new Set<TaskLiveStatus>([
  "done",
  "stopped",
  "out_of_credits",
  "error",
]);

// ── styles (porcelain board idiom; theme vars so dark mode rides along) ─────

/** Presence card: corner bloom in the state color over the elevated surface. */
function surfaceFor(cc: string): CSSProperties {
  return {
    position: "relative",
    borderRadius: 14,
    padding: "14px 18px",
    background: `radial-gradient(34% 115% at 100% 0%, color-mix(in srgb, ${cc} 11%, transparent) 0%, transparent 74%), var(--bg-elevated)`,
    boxShadow: `0 1px 2px rgba(0,0,0,0.05), 0 4px 14px rgba(0,0,0,0.06), 0 2px 10px color-mix(in srgb, ${cc} 6%, transparent)`,
  };
}

function kickerFor(cc: string): CSSProperties {
  return {
    fontFamily: "var(--font-mono), ui-monospace, monospace",
    fontSize: 9.5,
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    display: "inline-block",
    marginBottom: 7,
    color: `color-mix(in srgb, ${cc} 78%, var(--fg-default) 22%)`,
  };
}

const youLine: CSSProperties = {
  fontSize: 13,
  color: "var(--fg-subtle)",
  paddingBottom: 9,
  borderBottom: "1px solid var(--border-subtle)",
  marginBottom: 10,
};
const youLineStrong: CSSProperties = {
  color: "var(--fg-muted)",
  fontWeight: 500,
};

const footRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 11,
};
const spacer: CSSProperties = { flex: 1 };
const btnBase: CSSProperties = {
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: 9,
  padding: "7px 13px",
  cursor: "pointer",
  border: "1px solid transparent",
};
const ghostBtn: CSSProperties = {
  ...btnBase,
  background: "transparent",
  color: "var(--fg-muted)",
  borderColor: "var(--border-muted)",
};
/** The settled answer's footer line: the same input continues this
 *  conversation (the lane threads follow-ups) — said out loud, because a
 *  reader used to one-input-one-task asks "will it start a new task?"
 *  (Ankur, 08-27). */
const footHint: CSSProperties = {
  fontSize: 12,
  color: "var(--fg-subtle)",
  alignSelf: "center",
};
const closeBtn: CSSProperties = {
  position: "absolute",
  top: 9,
  right: 9,
  width: 26,
  height: 26,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: 0,
  borderRadius: 8,
  cursor: "pointer",
  color: "var(--fg-faint)",
  fontSize: 16,
  lineHeight: 1,
  padding: 0,
};
const primaryBtn: CSSProperties = {
  ...btnBase,
  background: "var(--fg-default)",
  color: "var(--bg-base)",
};

/** Speech, at the answer card's size. A sentence set at title weight reads as
 *  a heading, which is the thing the card stopped being. */
const sayStyle: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "var(--fg-default)",
  marginBottom: 6,
};

const titleStyle: CSSProperties = {
  fontSize: 15.5,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  color: "var(--fg-default)",
  marginBottom: 4,
};
const metaStyle: CSSProperties = {
  fontSize: 12.5,
  color: "var(--fg-muted)",
  display: "flex",
  alignItems: "center",
  gap: 7,
};

export function ComposerInlineCard({
  ui,
  question,
  onDismiss,
  onRetryClassic,
  onUpgrade,
  retryable = true,
  trouble = null,
  onClosePanel,
}: ComposerInlineCardProps) {
  const router = useRouter();

  // Copy replaces Dismiss on the settled answer (founder, 08-03): the useful
  // action is taking the answer with you; closing lives on the (x) and Esc.
  const answerText =
    ui.phase === "answered" || ui.phase === "answering" ? ui.text : "";
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setCopied(false);
  }, [answerText]);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(answerText)
      .then(() => setCopied(true))
      .catch(() => {
        // Clipboard permission denied — the button simply stays "Copy".
      });
  };

  // Heavy answers scroll INSIDE the card (founder, 08-06: the card was growing
  // past the top of the screen). While streaming, follow the newest text —
  // but only when the reader is already at the tail; scrolling up to re-read
  // mid-stream pins the view (the standard chat-follow contract).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const streamingText = ui.phase === "answering" ? ui.text : null;
  useEffect(() => {
    if (streamingText === null) return;
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromTail = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromTail < 160) el.scrollTop = el.scrollHeight;
  }, [streamingText]);

  // Scroll chaining. An `overflow: auto` box is a scroll container even when
  // its content fits, and `overscroll-behavior: contain` on a container at its
  // boundary swallows the wheel — hovering a SHORT card froze the page's
  // scroll (Ankur, 08-27, on the Bash home, where the card sits in the page
  // rather than floating over it). Contain only while the body actually
  // overflows: measured on every content change, and on resize because the
  // cap is viewport-relative.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      el.dataset.overflows =
        el.scrollHeight > el.clientHeight ? "true" : "false";
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ui.phase, streamingText]);

  // Esc closes the card in its settled states, same availability as the
  // visible close affordances. The card CLAIMS the key: preventDefault, in
  // the window's capture phase, so the Line's own Escape (the Dock's, bubble
  // phase, yielding to defaultPrevented) leaves the line up when the card
  // above it closes; otherwise one Esc on a settled card also hung up.
  const closable =
    ui.phase === "answered" ||
    ui.phase === "error" ||
    ui.phase === "credits" ||
    (ui.phase === "routed" && ui.action.kind === "open_url") ||
    (ui.phase === "routed" &&
      ui.action.kind === "nami_saved" &&
      Boolean(ui.action.say));
  // A panel the deck DRAWS claims Escape for itself (PanelDeck, window
  // capture, composer panels §3.4): while one is up the card does not listen
  // at all, so one press closes the panel and the next closes the card.
  // Registration order must not decide that — a panel arriving AFTER the card
  // mounted registers its listener last, so yielding to a claimed press could
  // not save the card from closing first. The card yields to one anyway (the
  // Line's paper, a focused field): one Escape never closes two things.
  const panelClaimsEscape = (ui.panels ?? []).some((p) =>
    DRAWN_KINDS.has(p.kind)
  );
  useEffect(() => {
    if (!closable || panelClaimsEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onDismiss();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [closable, panelClaimsEscape, onDismiss]);

  // Stable: the deck registers its Escape listener per identity of this
  // callback, and an inline arrow would re-register it on every render.
  const handleClosePanel = useCallback(
    (kind: ComposerPanelKind) => onClosePanel?.(kind),
    [onClosePanel]
  );

  // "Saved to Nami" needs no reply — auto-dismiss after the confirm beat. A
  // save that carries its own sentence (a link sent alone) asks them what to
  // do with it, so it stays until they answer or close it.
  const isNami = ui.phase === "routed" && ui.action.kind === "nami_saved";
  const namiAsks =
    ui.phase === "routed" &&
    ui.action.kind === "nami_saved" &&
    Boolean(ui.action.say);
  useEffect(() => {
    if (!isNami || namiAsks) return;
    const t = setTimeout(() => onDismiss(), NAMI_AUTODISMISS_MS);
    return () => clearTimeout(t);
  }, [isNami, namiAsks, onDismiss]);

  // The receipt (task_created / task_followup) — status rides the shared truth
  // ladder (live phase → story spine → lifecycle), the SAME truth the board
  // renders. A newborn with no row anywhere is Queued, never Running — the
  // card must not claim more than AGENT_START has delivered (team, 08-04).
  const receipt =
    ui.phase === "routed" &&
    (ui.action.kind === "task_created" || ui.action.kind === "task_followup")
      ? ui.action
      : null;
  const receiptLive = useTaskStore((s) =>
    receipt ? s.tasks.get(receipt.taskId) : undefined
  );
  const receiptRow = useHistoryStore((s) =>
    receipt ? s.taskHistory.find((t) => t.id === receipt.taskId) : undefined
  );
  const receiptStatus: TaskLiveStatus = taskLiveStatus({
    livePhase: receiptLive?.phase,
    liveError: Boolean(receiptLive?.error),
    storyPhase: receiptRow?.storyPhase,
    lifecycleState: receiptRow?.lifecycleState,
  });
  /** The model's own sentence for this request, when it wrote one. */
  const receiptSay =
    receipt && typeof receipt.say === "string" && receipt.say.trim()
      ? receipt.say.trim()
      : null;
  const receiptModel = useMemo(
    () =>
      receipt && receipt.kind === "task_created" && receipt.model
        ? labelForModel(receipt.model)
        : null,
    [receipt]
  );
  // A substituted model is never silent (founder 08-13): when the server ran
  // the task on something other than the user's pick — a text-only pick on
  // the computer lane — the receipt says so in the user's own vocabulary.
  const receiptSwap = useMemo(
    () =>
      receipt &&
      receipt.kind === "task_created" &&
      receipt.substitutedFrom &&
      receipt.model
        ? {
            from: shortLabelForModel(receipt.substitutedFrom),
            to: shortLabelForModel(receipt.model),
          }
        : null,
    [receipt]
  );

  // Receipt dissolve: after the linger the card folds away. It still stamps
  // the dots store, but the rim strip that rendered it is retired (founder
  // 08-12) — the write is inert until a surface reads the store again.
  // Trouble landing beneath the receipt (the Line) restarts the beat, so a
  // late failure is read, not folded away with the receipt it arrived on.
  // Keyed on whether trouble is there, a primitive: the line may be a node
  // built fresh each render, and identity churn must never restart the beat.
  const receiptTaskId = receipt?.taskId ?? null;
  const hasTrouble = Boolean(trouble);
  const receiptTitle = receipt?.title ?? "";
  useEffect(() => {
    if (!receiptTaskId) return;
    const model =
      receipt && receipt.kind === "task_created" ? receipt.model : undefined;
    const t = setTimeout(() => {
      useComposerDotsStore
        .getState()
        .add({ taskId: receiptTaskId, title: receiptTitle, model });
      onDismiss();
    }, RECEIPT_LINGER_MS);
    return () => clearTimeout(t);
    // Keyed on the task id and the trouble's presence — `receipt` is re-derived each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptTaskId, hasTrouble, onDismiss]);

  const stripText =
    ui.phase === "thinking"
      ? "Thinking"
      : ui.phase === "routing"
        ? narrationForTool(ui.tool)
        : null;

  return (
    <div
      role="status"
      aria-label="Inline answer"
      data-testid="composer-inline-card"
    >
      <style>{`
        .cinl-shimmer {
          font-size: 13.5px; font-weight: 500;
          background: linear-gradient(90deg, var(--fg-muted) 0%, color-mix(in srgb, ${LAVENDER} 72%, var(--fg-default) 28%) 35%, var(--fg-default) 50%, color-mix(in srgb, ${LAVENDER} 72%, var(--fg-default) 28%) 65%, var(--fg-muted) 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
          animation: cinlSweep 2s ease-in-out infinite;
        }
        @keyframes cinlSweep { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes cinlPulse { 0%, 100% { opacity: 1; } 50% { opacity: .35; } }
        @keyframes cinlCaret { 50% { opacity: 0; } }
        .cinl-live-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--kind-green); animation: cinlPulse 1.6s ease-in-out infinite; flex: none; }
        .cinl-live-dot.done { animation: none; }
        .cinl-caret { display: inline-block; width: 7px; height: 15px; border-radius: 2px; vertical-align: -2px; background: color-mix(in srgb, var(--hb-name) 60%, transparent); animation: cinlCaret 1s steps(2) infinite; }
        .cinl-md { font-size: 14px; line-height: 1.6; color: var(--fg-default); }
        .cinl-md > :first-child { margin-top: 0; }
        .cinl-md > :last-child { margin-bottom: 0; }
        .cinl-md p { margin: 0 0 8px; }
        .cinl-md strong { font-weight: 650; }
        .cinl-md ul, .cinl-md ol { margin: 6px 0 8px; padding-left: 20px; }
        .cinl-md li { margin: 3px 0; }
        .cinl-md a { color: var(--hb-name); text-decoration: underline; text-underline-offset: 2px; }
        .cinl-md code { font-family: var(--font-mono), ui-monospace, monospace; font-size: 12px; background: var(--bg-muted); padding: 1px 5px; border-radius: 5px; }
        .cinl-md pre { background: var(--bg-subtle); border: 1px solid var(--border-subtle); border-radius: 9px; padding: 10px 12px; overflow-x: auto; margin: 7px 0; }
        .cinl-md pre code { background: none; padding: 0; font-size: 12px; }
        .cinl-md h1, .cinl-md h2, .cinl-md h3 { font-size: 14px; font-weight: 650; margin: 9px 0 4px; }
        .cinl-md blockquote { margin: 7px 0; padding-left: 11px; border-left: 2px solid var(--border-muted); color: var(--fg-muted); }
        .cinl-answer-cap { display: flex; flex-direction: column; max-height: min(80vh, calc(100vh - 180px)); }
        .cinl-scroll { overflow-y: auto; min-height: 0; }
        .cinl-scroll[data-overflows="true"] { overscroll-behavior: contain; }
        .cinl-receipt { display: flex; align-items: center; justify-content: space-between; gap: 18px; width: 100%; text-align: left; border: 0; font: inherit; background: none; padding: 0; cursor: pointer; }
        .cinl-chev { color: var(--fg-faint); font-size: 15px; flex: none; }
        @media (prefers-reduced-motion: reduce) {
          .cinl-shimmer { animation: none; -webkit-text-fill-color: var(--fg-muted); }
          .cinl-live-dot, .cinl-caret { animation: none; }
        }
      `}</style>

      {/* Pre-response THIN STRIP — shimmer only, copy walks per tool event. */}
      {stripText !== null && (
        <div
          style={{ ...surfaceFor(LAVENDER), padding: "12px 18px" }}
          data-testid="cinl-strip"
        >
          <span className="cinl-shimmer">{stripText}</span>
        </div>
      )}

      {/* Inline answer (streaming or settled) — Bash's amber. Capped at ~80%
          of the viewport (founder, 08-06): heavy answers scroll INSIDE the
          body while the kicker/question header and the Copy footer stay
          pinned. The calc arm keeps the card clear of the dock on short
          windows, where a bare 80vh would still climb off-screen. */}
      {(ui.phase === "answering" || ui.phase === "answered") && (
        <div
          className="cinl-answer-cap"
          style={surfaceFor("var(--hb-name)")}
          data-testid="cinl-answer"
        >
          <span style={kickerFor("var(--hb-name)")}>Bash</span>
          {question && (
            <div style={youLine}>
              <span style={youLineStrong}>{question}</span>
            </div>
          )}
          <div
            className="cinl-scroll"
            ref={scrollRef}
            data-testid="cinl-scroll"
          >
            <div
              className="cinl-md"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(ui.text) }}
            />
            {ui.phase === "answering" && (
              <span className="cinl-caret" aria-hidden />
            )}
          </div>
          {ui.phase === "answered" && (
            <>
              <button
                type="button"
                style={closeBtn}
                onClick={onDismiss}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
              <div style={footRow}>
                <span style={footHint}>Keep talking to Bash to continue</span>
                <span style={spacer} />
                <button type="button" style={ghostBtn} onClick={handleCopy}>
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* The panels (composer panels, spec 2026-09-19 §3.4): the same cards as
          the pill, below the answer. The task page has no history and no tray,
          so those two print their words — and the margin rides what the deck
          DRAWS (their words included), so a kind no host draws leaves no box. */}
      {deckDraws(ui.panels, true) && (
        <div className="mt-2" data-testid="cinl-panels">
          <PanelDeck
            panels={ui.panels}
            onClose={handleClosePanel}
            onOpen={(route) => {
              router.push(route);
              onDismiss();
            }}
            onUndo={undoRunApi}
            words
          />
        </div>
      )}

      {/* Task receipt — the card is the door; no navigation yank. */}
      {receipt && (
        <div style={surfaceFor("var(--kind-green)")} data-testid="cinl-receipt">
          <button
            type="button"
            className="cinl-receipt"
            aria-label={`Open task: ${receipt.title}`}
            onClick={() => {
              useComposerDotsStore.getState().add({
                taskId: receipt.taskId,
                title: receipt.title,
                model:
                  receipt.kind === "task_created" ? receipt.model : undefined,
              });
              router.push(`/task/${receipt.taskId}`);
              onDismiss();
            }}
          >
            <span className="min-w-[0px]">
              {/* THE SENTENCE IS THE CARD (founder mock 09-02, option C).
                  `say` is the one line the model wrote for THIS request, and
                  it already ships verbatim to every channel. When it is here,
                  the kicker and the title BOTH go: "Task created" narrates the
                  machinery the character may not name, and the title beneath
                  it is the verbatim echo the same house rule forbids. What
                  stays is what the web genuinely adds over a phone — the live
                  status and the door.

                  No `say` (an older server, or the voice failed) falls back to
                  exactly what shipped before. Same law as the channels: the
                  facts stay the rail's, and a voice failure is never a blank. */}
              {receiptSay ? (
                <span className="block" style={sayStyle}>
                  {receiptSay}
                </span>
              ) : (
                <>
                  <span style={kickerFor("var(--kind-green)")}>
                    {receipt.kind === "task_created"
                      ? "Task created"
                      : "Task updated"}
                  </span>
                  <span className="block" style={{ ...titleStyle }}>
                    {receipt.title}
                  </span>
                </>
              )}
              <span style={metaStyle}>
                <span
                  className={cn(
                    "cinl-live-dot",
                    RECEIPT_SETTLED.has(receiptStatus) ? " done" : ""
                  )}
                  style={
                    RECEIPT_DOT_COLOR[receiptStatus]
                      ? { background: RECEIPT_DOT_COLOR[receiptStatus] }
                      : undefined
                  }
                  aria-hidden
                />
                {STATUS_WORD[receiptStatus]}
                {receiptModel && (
                  <>
                    <span className="text-fg-faint">·</span>
                    <span className="text-fg-faint">{receiptModel}</span>
                  </>
                )}
                <span className="text-fg-faint">·</span>
                <span className="text-fg-faint">just now</span>
              </span>
              {receiptSwap && (
                <span
                  data-testid="cinl-model-swap"
                  className="block mt-1 text-3 leading-[1.45] text-fg-muted"
                >
                  {receiptSwap.from} can&apos;t see the screen, so this one runs
                  on {receiptSwap.to}
                </span>
              )}
            </span>
            <span className="cinl-chev" aria-hidden>
              ›
            </span>
          </button>
          {trouble && (
            <div
              data-testid="cinl-trouble"
              className="mt-2 text-3 leading-[1.45] text-fg-muted"
            >
              {trouble}
            </div>
          )}
        </div>
      )}

      {/* Saved to Nami (auto-dismisses) — Nami's emerald. */}
      {isNami && ui.phase === "routed" && ui.action.kind === "nami_saved" && (
        <div
          style={surfaceFor("var(--color-nami-700)")}
          data-testid="cinl-nami"
        >
          <span style={kickerFor("var(--color-nami-700)")}>Saved to Nami</span>
          <div style={titleStyle}>
            {ui.action.say || "Filed. Nothing to do"}
          </div>
          {ui.action.summary && (
            <div className="text-3 text-fg-subtle">{ui.action.summary}</div>
          )}
          {ui.action.say && (
            <div className="mt-2">
              <button
                type="button"
                className="text-3.5 text-fg-muted hover:text-fg-default"
                onClick={onDismiss}
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}

      {/* The connect link (2a): the tab opened on its own when the browser allowed it; when it did
          not, this is the tap. The same https belt the opener uses, because this anchor is the one
          place the URL becomes a thing the person can click. */}
      {ui.phase === "routed" &&
        ui.action.kind === "open_url" &&
        /^https:\/\//.test(ui.action.url) && (
          <div
            style={surfaceFor("var(--color-fg-default)")}
            data-testid="cinl-open-url"
          >
            <span style={kickerFor("var(--color-fg-default)")}>
              {ui.action.label}
            </span>
            <a
              href={ui.action.url}
              target="_blank"
              rel="noopener noreferrer"
              style={titleStyle}
            >
              Open the link
            </a>
            <div className="mt-2">
              <button
                type="button"
                className="text-3.5 text-fg-muted hover:text-fg-default"
                onClick={onDismiss}
              >
                Done
              </button>
            </div>
          </div>
        )}

      {/* Out of credits (founder 09-02) — the door is Upgrade, never a retry
          the server would refuse again. */}
      {ui.phase === "credits" && (
        <div style={surfaceFor("var(--kind-amber)")} data-testid="cinl-credits">
          <span style={kickerFor("var(--kind-amber)")}>Out of credits</span>
          {question && (
            <div style={youLine}>
              <span style={youLineStrong}>{question}</span>
            </div>
          )}
          <div className="text-3 text-fg-muted">{ui.message}</div>
          <div style={footRow}>
            <button type="button" style={ghostBtn} onClick={onDismiss}>
              Dismiss
            </button>
            <span style={spacer} />
            <button type="button" style={primaryBtn} onClick={onUpgrade}>
              Upgrade
            </button>
          </div>
        </div>
      )}

      {/* Error — the single retry rides the CLASSIC lane. */}
      {ui.phase === "error" && (
        <div style={surfaceFor("var(--kind-red)")} data-testid="cinl-error">
          <span style={kickerFor("var(--kind-red)")}>Not sent</span>
          {question && (
            <div style={youLine}>
              <span style={youLineStrong}>{question}</span>
            </div>
          )}
          <div className="text-3 text-fg-muted">
            {retryable
              ? "That didn't go through. Retry runs it as a task instead."
              : "That didn't go through."}
          </div>
          <div style={footRow}>
            <button type="button" style={ghostBtn} onClick={onDismiss}>
              Dismiss
            </button>
            {retryable && (
              <>
                <span style={spacer} />
                <button
                  type="button"
                  style={primaryBtn}
                  onClick={onRetryClassic}
                >
                  Retry as a task
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
